/**
 * Plan Service
 *
 * Service for managing plan-related operations including CRUD operations,
 * plan sharing, and filtering. Extends the base
 * CrudService to provide plan-specific functionality with proper
 * validation and error handling.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { clearStartWorkoutOptionsCache } from '../utils/workout/start-options/start-answer';
import { validatePlanSchedule } from '../validation/plan-schedule.schemas';
import { validateShareResourceConfig } from '../validation/share.schemas';
import {
  validatePlanTemplate,
  type PlanTemplateInput,
} from '../validation/template.schemas';

import type {
  SearchRequest,
  ShareResourceConfig,
  PaginatedResponse,
  CreatedApiResponse,
  GetApiResponse,
  PlanTemplate,
  PlanTemplateDetail,
  PlanScheduleResponse,
  PlanScheduleMap,
  ID,
} from '../types';

// =============================================================================
// PLAN SERVICE CLASS
// =============================================================================

/**
 * PlanService class providing comprehensive plan management functionality
 * Extends CrudService to inherit common CRUD operations and adds plan-specific methods
 */
export class PlanService extends CrudService<
  PlanTemplateDetail,
  PlanTemplate,
  PlanTemplateInput
> {
  constructor() {
    super({
      baseUrl: '/plan',
      enableCaching: true,
      cacheTTL: 5 * 60 * 1000, // 5 minutes cache TTL
      cacheVariesByUnit: true, // plan workout/exercise weights arrive in the user's unit
    });
  }

  /**
   * Every write that drops this service's cached reads also drops the
   * start-workout flow's remembered answer: the flow's schedule and suggestions
   * come from the plans, their schedules and which one is active. Without it
   * the bolt opens on the old answer and holds the new one back as a pending
   * pill.
   */
  protected override invalidateCache(operations: string[], id?: ID): void {
    super.invalidateCache(operations, id);
    clearStartWorkoutOptionsCache();
  }

  // ---------------------------------------------------------------------------
  // CORE PLAN OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get plans with advanced filtering and pagination
   * Supports filtering by difficulty, status, and search terms
   *
   * @param params - Search and filter parameters
   * @returns Promise resolving to paginated plan list
   *
   * @example
   * ```typescript
   * const plans = await planService.getPlans({
   *   difficulty_level: 'Intermediate',
   *   status: 'active',
   *   term: 'upper body',
   *   paginate: false, // the whole list; omit it for the API's first page
   * });
   * ```
   */
  public async getPlans(
    params: SearchRequest
  ): Promise<PaginatedResponse<PlanTemplate>> {
    try {
      // Validate and transform search parameters
      const queryParams = this.transformSearchParams(params);

      // One page (the API's default) unless the caller asks for everything
      if (params.paginate !== false) {
        return await this.getPaginated(queryParams);
      }

      // Everything, via getAll's paginate=false
      const results = await this.getAll(queryParams);

      // Transform to paginated response format for consistency
      return this.transformToPaginated(results);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      rethrowTyped(error, 'Unable to fetch plans. Please try again.');
    }
  }

  /**
   * Get detailed plan information by ID
   * Returns complete plan template with workouts and metadata
   *
   * @param id - Plan identifier
   * @returns Promise resolving to plan details
   *
   * @example
   * ```typescript
   * const plan = await planService.getPlanById('plan-123');
   * ```
   */
  public async getPlanById(id: ID): Promise<PlanTemplateDetail> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      const plan = await this.getById(id);
      return this.transformResponse(plan);
    } catch (error) {
      console.error(`Failed to fetch plan ${id}:`, error);
      rethrowTyped(
        error,
        `Unable to fetch plan details. Please check the plan ID and try again.`
      );
    }
  }

  /**
   * Create a new plan with validation
   * Validates plan data before sending to API
   *
   * @param data - Plan creation data
   * @returns Promise resolving to created plan
   *
   * @example
   * ```typescript
   * const newPlan = await planService.create({
   *   name: 'Upper Body Strength',
   *   description: 'Focus on chest, shoulders, and arms',
   *   difficulty: 'Intermediate',
   * });
   * ```
   */
  public override async create(data: PlanTemplateInput): Promise<PlanTemplate> {
    try {
      // Validate plan data
      this.validatePlanData(data);

      // Transform data before sending to API
      const transformedData = this.transformRequest(data);

      // Delegate to base CRUD create implementation
      const createdPlan = await super.create(transformedData);
      return createdPlan;
    } catch (error) {
      console.error('Failed to create plan:', error);
      rethrowTyped(
        error,
        'Unable to create plan. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing plan
   * Validates updated data and merges with existing plan
   *
   * @param id - Plan identifier
   * @param data - Updated plan data (partial updates supported)
   * @returns Promise resolving to updated plan
   *
   * @example
   * ```typescript
   * const updatedPlan = await planService.updatePlan('plan-123', {
   *   name: 'Advanced Upper Body',
   *   difficulty: 'Hard'
   * });
   * ```
   */
  public async updatePlan(
    id: ID,
    data: PlanTemplateInput
  ): Promise<PlanTemplate> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      // Validate plan data using full template validation
      this.validatePlanData(data);

      // Transform data before sending to API
      const transformedData = this.transformUpdateRequest(data);

      const updatedPlan = await this.update(id, transformedData);
      return updatedPlan;
    } catch (error) {
      console.error(`Failed to update plan ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update plan. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a plan by ID
   * Permanently removes the plan and all associated data
   *
   * @param id - Plan identifier
   * @returns Promise resolving when deletion is complete
   *
   * @example
   * ```typescript
   * await planService.deletePlan('plan-123');
   * ```
   */
  public async deletePlan(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete plan ${id}:`, error);
      rethrowTyped(error, 'Unable to delete plan. Please try again.');
    }
  }

  /**
   * Restore a soft-deleted plan by ID
   * Used to undo a recent deletion before the record is pruned
   *
   * @param id - Plan identifier
   * @returns Promise resolving when the restore completes
   *
   * @example
   * ```typescript
   * await planService.restorePlan('plan-123');
   * ```
   */
  public async restorePlan(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      await this.undoDelete('plan', id);
    } catch (error) {
      console.error(`Failed to restore plan ${id}:`, error);
      rethrowTyped(error, 'Unable to restore plan. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // ACTIVATION
  // ---------------------------------------------------------------------------

  /**
   * Make a plan the one being followed.
   *
   * Its own endpoint, never a plan update: `PUT /plan/{id}` replaces the whole
   * resource, so an update carrying only the active flag drops the plan's
   * workouts.
   *
   * @param id - Plan identifier
   * @returns Promise resolving when the plan is active (204, no body)
   *
   * @example
   * ```typescript
   * await planService.activatePlan('plan-123');
   * ```
   */
  public async activatePlan(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      await this.setPlanActive(id, 'activate');
    } catch (error) {
      console.error(`Failed to activate plan ${id}:`, error);
      rethrowTyped(error, 'Unable to activate plan. Please try again.');
    }
  }

  /**
   * Stop following a plan. Its progress is kept.
   *
   * @param id - Plan identifier
   * @returns Promise resolving when the plan is inactive (204, no body)
   *
   * @example
   * ```typescript
   * await planService.deactivatePlan('plan-123');
   * ```
   */
  public async deactivatePlan(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      await this.setPlanActive(id, 'deactivate');
    } catch (error) {
      console.error(`Failed to deactivate plan ${id}:`, error);
      rethrowTyped(error, 'Unable to deactivate plan. Please try again.');
    }
  }

  /**
   * Drop every cached plan read: the lists and every detail.
   *
   * Public for writers outside this service whose change shows on a plan —
   * answering a change proposal can rewrite a plan's workouts or schedule, and
   * clears its `suggested_schedule` — which the base class's write-path
   * invalidation cannot see.
   */
  public invalidatePlanCache(): void {
    this.invalidateCache([
      'getAll',
      'getPaginated',
      'getById',
      'search',
      'count',
    ]);
  }

  private async setPlanActive(
    id: ID,
    action: 'activate' | 'deactivate'
  ): Promise<void> {
    await apiClient.patch(`/plan/${id}/${action}`);

    // A raw write, so the cached reads have to be dropped by hand. Every one
    // of them, not just this plan's: the active flag is on the lists too.
    this.invalidateCache([
      'getAll',
      'getPaginated',
      'getById',
      'search',
      'count',
    ]);
  }

  // ---------------------------------------------------------------------------
  // SCHEDULE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get everything needed to edit a plan's schedule: the plan's workouts, the
   * upcoming sessions, and the elapsed ones.
   *
   * Separate from `getPlanById` because the two answer different questions. The
   * plan detail lists the schedule's *occurrences*, so a workout that has never
   * been scheduled is absent from it — which is exactly the workout someone
   * opening the editor is most likely to be reaching for.
   *
   * @param id - Plan identifier
   * @returns Promise resolving to the schedule editor payload
   *
   * @example
   * ```typescript
   * const { workouts, upcoming, today } = await planService.getPlanSchedule('plan-123');
   * ```
   */
  public async getPlanSchedule(id: ID): Promise<PlanScheduleResponse> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      // Doubly nested on purpose: `response.data` is the HTTP body, and the
      // body is Laravel's `{ data: … }` envelope. Reading one level shallower
      // yields the envelope itself, whose `workouts`/`today` are undefined —
      // which fails downstream in the view model rather than here.
      const response = await apiClient.get<
        GetApiResponse<PlanScheduleResponse>
      >(`/plan/${id}/workouts`);

      if (!response.data?.data) {
        throw new Error(`Failed to load the schedule for plan ${id}`);
      }

      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch schedule for plan ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to load this plan’s schedule. Please try again.'
      );
    }
  }

  /**
   * Replace a plan's upcoming sessions.
   *
   * `schedule` must carry the **complete** future, not a delta — the API
   * replaces what it finds, so a date left out is a session removed. It must
   * also contain no date before today; the API refuses those rather than
   * dropping them, so half an edit never lands silently. An empty map is legal
   * and means "clear my upcoming sessions", leaving the past intact.
   *
   * @param id - Plan identifier
   * @param schedule - Complete future schedule, `YYYY-MM-DD` to workout hashid
   * @returns Promise resolving when the write completes
   *
   * @example
   * ```typescript
   * await planService.updatePlanSchedule('plan-123', {
   *   '2026-08-17': 'wk_7Bq3',
   *   '2026-08-19': 'wk_2Xd9',
   * });
   * ```
   */
  public async updatePlanSchedule(
    id: ID,
    schedule: PlanScheduleMap
  ): Promise<void> {
    if (!id) {
      throw new Error('Plan ID is required');
    }

    try {
      // Validated before it is sent, like every other write here. Worth more
      // than saving a round trip: the write replaces the whole future, so a
      // payload carrying a past date would have the entire edit refused, and
      // the user would be looking at a schedule that never landed.
      const body = validatePlanSchedule({ schedule });

      await apiClient.patch<void>(`/plan/${id}/schedule`, body);

      // A raw write bypasses CrudService's own invalidation, and this one moves
      // the plan payload underneath us: the detail response builds its workout
      // entries from the schedule, so a cached copy would come back describing
      // the sessions we just replaced.
      this.invalidateCache(['getAll', 'getPaginated', 'getById', 'search'], id);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod('plan schedule', error);
      }

      console.error(`Failed to update schedule for plan ${id}:`, error);
      rethrowTyped(error, 'Unable to save your schedule. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // ADVANCED PLAN OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Share a plan with one or more people, optionally with a schedule.
   *
   * The whole recipient list goes in one request — the API takes up to ten.
   *
   * `schedule` is the days the recipients are being asked to train, dates to
   * workout hashids. It is **their** schedule, not the sharer's: accepting the
   * invitation copies the plan and these dates onto the recipient, who has no
   * history on it. Nothing about the sharer's own progress belongs here.
   *
   * @param data - Recipients, the resource, an optional note and schedule
   *
   * @example
   * ```typescript
   * await planService.sharePlan({
   *   share_with: ['john_doe', 'jane_smith'],
   *   share_id: 'plan-123',
   *   share_type: 'plan',
   *   note: 'Start Monday — ease into it.',
   *   include_notes: true,
   *   schedule: {
   *     '2026-08-19': 'wk_7Bq3',
   *     '2026-08-21': 'wk_2Xd9',
   *   },
   * });
   * ```
   */
  public async sharePlan(data: ShareResourceConfig): Promise<void> {
    try {
      // Validate share configuration
      this.validatePlanShareConfig(data);

      await apiClient.post<CreatedApiResponse>('/share', data);
    } catch (error) {
      console.error(
        `Failed to share resource ${data.share_type} with ID, ${data.share_id}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to share plan. Please check your sharing configuration and try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION METHODS
  // ---------------------------------------------------------------------------

  /**
   * Validate plan data using Zod schema
   * Ensures data meets requirements before API calls
   *
   * @param data - Plan data to validate
   * @throws Error if validation fails
   */
  protected validatePlanData(data: PlanTemplateInput): void {
    try {
      validatePlanTemplate(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the plan", error);
      }
      throw new InvalidDataError(
        "Couldn't save the plan: the data is in an unexpected format"
      );
    }
  }

  /**
   * Validate share configuration
   * Ensures sharing parameters are valid and complete
   *
   * @param config - Share configuration to validate
   * @throws Error if validation fails
   */
  protected validatePlanShareConfig(config: ShareResourceConfig): void {
    if (config.share_type !== 'plan') {
      throw new InvalidDataError('Share type must be "plan"');
    }

    try {
      validateShareResourceConfig(config);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't share the plan", error);
      }
      throw new InvalidDataError(
        "Couldn't share the plan: the data is in an unexpected format"
      );
    }
  }

  /**
   * Transform request data before sending to API
   * Formats data according to API expectations
   *
   * @param data - Original request data
   * @returns Transformed request data
   */
  protected override transformRequest(
    data: PlanTemplateInput
  ): PlanTemplateInput {
    // Add any necessary data transformations here
    return {
      ...data,
    };
  }

  /**
   * Transform update request data before sending to API
   * Formats partial data according to API expectations
   *
   * @param data - Original partial update data
   * @returns Transformed update request data
   */
  protected transformUpdateRequest(data: PlanTemplateInput): PlanTemplateInput {
    // Add any necessary data transformations here
    return {
      ...data,
    };
  }

  /**
   * Transform response data received from API
   * Formats data for frontend consumption
   *
   * @param data - Raw API response data
   * @returns Transformed response data
   */
  protected override transformResponse(
    data: PlanTemplateDetail
  ): PlanTemplateDetail {
    // Add any necessary response transformations here
    // For example, parsing dates or converting values
    return {
      ...data,
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default plan service instance
 * Pre-configured and ready to use throughout the application
 */
export const planService = new PlanService();
